import { IconButton, Popover } from "@controls";
import { useLocalstorage } from "@hooks";
import { LineColumnsRegular } from "@icons";
import { useAnnounce } from "@src/components/live-announcer";
import { NO_INTERPOLATION_ESCAPE } from "@src/raya-lucaria/i18n/interpolation";
import { useEffect, useId } from "react";

import { TableContextType, useTableContext } from "../context";
import Select from "../select";
import { getColumnLabel } from "../utils";

export default function TableColumnVisibilityToggle() {
  const { t, table, tableId } = useTableContext() as TableContextType;
  const idPrefix = useId();
  const { setState: setTableState } = table;
  const columns = table.getAllColumns();
  const announce = useAnnounce();

  const [preservedColumnVisibility, setPreservedColumnVisibility] =
    useLocalstorage("TABLE-COLUMN-VISIBILITY-" + tableId?.toUpperCase(), "{}");

  useEffect(() => {
    if (!tableId) return;
    let preservedColumnVisibilityJson = {};
    try {
      preservedColumnVisibilityJson = JSON.parse(
        preservedColumnVisibility || "{}",
      );
    } catch (error) {
      console.error("Failed to parse preserved column visibility:", error);
    }

    setTableState((prev: any) => ({
      ...prev,
      columnVisibility: {
        ...prev.columnVisibility,
        ...preservedColumnVisibilityJson,
      },
    }));
  }, [preservedColumnVisibility, tableId, setTableState]);

  return (
    <Popover
      ariaLabel={t("toggleColumnVisibility")}
      trigger={
        <IconButton
          size="small"
          customIcon={<LineColumnsRegular />}
          aria-label={t("toggleColumnVisibility")}
        />
      }
      onClose={() => {
        if (!tableId) return;
        const currentColumnVisibility = table.getState().columnVisibility;
        setPreservedColumnVisibility(
          JSON.stringify(currentColumnVisibility, null, 2),
        );
      }}
    >
      <div className="flex flex-col gap-2 m-1">
        <p className="font-semibold">{t("columns")}</p>
        <form>
          {columns.map((column) => {
            const columnTitle = getColumnLabel(column);
            const toggleVisibility = column.getToggleVisibilityHandler();

            return (
              <div
                key={column.id}
                className="flex min-h-target items-center gap-target-pitch-gap"
              >
                <Select
                  row
                  id={`${idPrefix}-${column.id}`}
                  checked={column.getIsVisible()}
                  onChange={(event) => {
                    toggleVisibility(event);
                    announce(
                      event.target.checked
                        ? t("columnShown", {
                            column: columnTitle,
                            interpolation: NO_INTERPOLATION_ESCAPE,
                          })
                        : t("columnHidden", {
                            column: columnTitle,
                            interpolation: NO_INTERPOLATION_ESCAPE,
                          }),
                    );
                  }}
                />
                <label
                  htmlFor={`${idPrefix}-${column.id}`}
                  className="flex min-h-target flex-1 cursor-pointer items-center text-sm"
                >
                  {columnTitle}
                </label>
              </div>
            );
          })}
        </form>
      </div>
    </Popover>
  );
}
